import { useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
  StatusBadge,
  useUndo,
} from '@/design/components';
import { cn } from '@/lib/cn';
import { usePerm } from '@/access/usePerm';
import { errorMessage } from '@/lib/errors';
import { useEmployeeOptions } from '../api';
import {
  emptyTemplate,
  useAllContractTemplates,
  useTemplateFields,
  useTemplateMutations,
  type TemplateDraft,
} from './templatesApi';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * תבניות חוזי עבודה (מיגרציה 093).
 *
 * תבנית היא נוסח עם מקומות שמורים. בכרטיס של עובד בוחרים תבנית,
 * והנוסח נולד מלא בפרטים שלו — ולא מועתק ומוקלד מחדש לכל עובד.
 *
 * תחת עובדים ולא בהגדרות: מי שמוציא חוזה הוא מי שמגלה שהתבנית
 * צריכה שינוי, ובאותו רגע.
 */
export function ContractTemplatesPage() {
  const undo = useUndo();
  const canManage = usePerm('hr.private');
  const templates = useAllContractTemplates();
  const fields = useTemplateFields();
  const { data: options } = useEmployeeOptions();
  const { save, remove } = useTemplateMutations();

  const [draft, setDraft] = useState<TemplateDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  const rows = (templates.data ?? []).filter((template) => !undo.isPending(template.id));

  return (
    <AdminPage
      title="תבניות חוזים"
      subtitle="נוסח אחד לכל סקטור, שמתמלא לבד מכרטיס העובד"
      backTo="/admin/employees"
      actions={
        draft === null && (
          <Button variant="primary" onClick={() => setDraft(emptyTemplate())}>
            <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
            תבנית חדשה
          </Button>
        )
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {draft !== null && (
        <TemplateForm
          draft={draft}
          jobTitles={options?.jobTitles ?? []}
          fields={fields.data ?? []}
          isSaving={save.isPending}
          onChange={setDraft}
          onCancel={() => {
            setDraft(null);
            setError(null);
          }}
          onSubmit={() => {
            setError(null);
            save
              .mutateAsync(draft)
              .then(() => setDraft(null))
              .catch((caught: unknown) => setError(errorMessage(caught)));
          }}
        />
      )}

      {templates.isPending && <Skeleton className="h-32" />}
      {templates.isError && <ErrorState onRetry={() => void templates.refetch()} />}
      {templates.data?.length === 0 && draft === null && (
        <EmptyState
          title="אין עדיין תבניות"
          description="תבנית ראשונה — למשל חוזה שעתי לרצפה — ואפשר להוציא ממנה חוזה בלחיצה."
        />
      )}

      <ul className="grid list-none gap-3 p-0">
        {rows.map((template) => (
          <li
            key={template.id}
            className="grid gap-2 rounded-md border border-line bg-raised p-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-title font-medium">{template.name}</span>
              {!template.is_active && <StatusBadge tone="warn">כבויה</StatusBadge>}
              {template.job_title_ids.length === 0 ? (
                <span className="rounded-pill border border-muted px-2 py-0.5 text-label">
                  לכל התפקידים
                </span>
              ) : (
                template.job_title_ids.map((id) => (
                  <span
                    key={id}
                    className="rounded-pill border border-olive px-2 py-0.5 text-label"
                  >
                    {(options?.jobTitles ?? []).find((job) => job.id === id)?.name ?? 'תפקיד'}
                  </span>
                ))
              )}
              <span className="grow" />
              <Button
                variant="ghost"
                aria-label={`עריכת ${template.name}`}
                onClick={() => {
                  setError(null);
                  setDraft({
                    id: template.id,
                    name: template.name,
                    description: template.description ?? '',
                    contract_title: template.contract_title ?? '',
                    body: template.body,
                    is_active: template.is_active,
                    job_title_ids: template.job_title_ids,
                  });
                }}
              >
                <Pencil size={20} strokeWidth={1.75} aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                aria-label={`מחיקת ${template.name}`}
                onClick={() =>
                  undo.remove({
                    id: template.id,
                    message: `התבנית "${template.name}" נמחקה`,
                    perform: () => remove.mutateAsync(template.id),
                  })
                }
              >
                <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
              </Button>
            </div>

            {template.description && (
              <p className="m-0 text-body-sm text-muted">{template.description}</p>
            )}
            <p className="m-0 line-clamp-2 whitespace-pre-wrap text-body-sm text-muted">
              {template.body}
            </p>
          </li>
        ))}
      </ul>
    </AdminPage>
  );
}

function TemplateForm({
  draft,
  jobTitles,
  fields,
  isSaving,
  onChange,
  onCancel,
  onSubmit,
}: {
  draft: TemplateDraft;
  jobTitles: { id: string; name: string }[];
  fields: { token: string; label: string; group: string }[];
  isSaving: boolean;
  onChange: (draft: TemplateDraft) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const set = <K extends keyof TemplateDraft>(key: K, value: TemplateDraft[K]) =>
    onChange({ ...draft, [key]: value });

  /**
   * שתילת מקום שמור במקום הסמן, ולא בסוף הטקסט.
   *
   * מי שכותב סעיף באמצע ולוחץ "תאריך תחילת עבודה" מתכוון לכאן,
   * ולא לשורה האחרונה של המסמך.
   */
  const insert = (token: string) => {
    const area = bodyRef.current;
    const text = `{{${token}}}`;
    if (!area) {
      set('body', draft.body + text);
      return;
    }
    const start = area.selectionStart;
    const end = area.selectionEnd;
    const next = draft.body.slice(0, start) + text + draft.body.slice(end);
    set('body', next);
    requestAnimationFrame(() => {
      area.focus();
      area.setSelectionRange(start + text.length, start + text.length);
    });
  };

  const groups = [...new Set(fields.map((field) => field.group))];

  return (
    <Panel>
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1">
            <label htmlFor="tpl-name" className="text-label font-medium">
              שם התבנית *
            </label>
            <input
              id="tpl-name"
              value={draft.name}
              onChange={(event) => set('name', event.target.value)}
              placeholder="חוזה שעתי — רצפה"
              className={FIELD}
            />
            <p className="m-0 text-body-sm text-muted">לשימוש פנימי, בבחירת התבנית.</p>
          </div>

          <div className="grid gap-1">
            <label htmlFor="tpl-title" className="text-label font-medium">
              כותרת החוזה
            </label>
            <input
              id="tpl-title"
              value={draft.contract_title}
              onChange={(event) => set('contract_title', event.target.value)}
              placeholder="חוזה העסקה — {{full_name}}"
              className={FIELD}
            />
            <p className="m-0 text-body-sm text-muted">
              מה שהעובד/ת יראו. ריק = שם התבנית. אפשר מקומות שמורים גם כאן.
            </p>
          </div>
        </div>

        <div className="grid gap-1">
          <label htmlFor="tpl-desc" className="text-label font-medium">
            תיאור
          </label>
          <input
            id="tpl-desc"
            value={draft.description}
            onChange={(event) => set('description', event.target.value)}
            className={FIELD}
          />
        </div>

        <fieldset className="grid gap-2 rounded-md border border-line p-3">
          <legend className="px-1 text-label font-medium">לאילו תפקידים</legend>
          <p className="m-0 text-body-sm text-muted">
            בלי בחירה התבנית מוצעת לכולם. עם בחירה היא תופיע ראשונה אצל התפקידים האלה.
          </p>
          <div className="flex flex-wrap gap-2">
            {jobTitles.map((job) => {
              const active = draft.job_title_ids.includes(job.id);
              return (
                <button
                  key={job.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() =>
                    set(
                      'job_title_ids',
                      active
                        ? draft.job_title_ids.filter((id) => id !== job.id)
                        : [...draft.job_title_ids, job.id],
                    )
                  }
                  className={cn(
                    'min-h-11 cursor-pointer rounded-pill border px-4 text-label font-medium',
                    active
                      ? 'border-olive bg-olive text-on-olive'
                      : 'border-muted bg-transparent text-ink',
                  )}
                >
                  {job.name}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="grid gap-2">
          <label htmlFor="tpl-body" className="text-label font-medium">
            נוסח החוזה *
          </label>
          <textarea
            id="tpl-body"
            ref={bodyRef}
            rows={14}
            value={draft.body}
            onChange={(event) => set('body', event.target.value)}
            className={`${FIELD} py-2 font-mono`}
          />

          <div className="grid gap-2 rounded-md border border-line p-3">
            <span className="text-label font-medium">מקומות שמורים</span>
            <p className="m-0 text-body-sm text-muted">
              לחיצה שותלת במקום הסמן. בהוצאת החוזה הם מתמלאים מכרטיס העובד/ת, ושדה שחסר
              בכרטיס עוצר את השליחה ואומר מה חסר.
            </p>
            {groups.map((group) => (
              <div key={group} className="grid gap-1">
                <span className="text-body-sm text-muted">{group}</span>
                <div className="flex flex-wrap gap-2">
                  {fields
                    .filter((field) => field.group === group)
                    .map((field) => (
                      <button
                        key={field.token}
                        type="button"
                        onClick={() => insert(field.token)}
                        className="min-h-11 cursor-pointer rounded-pill border border-muted bg-transparent px-3 text-label text-ink"
                      >
                        {field.label}
                      </button>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <label className="flex min-h-11 items-center gap-2 text-body">
          <input
            type="checkbox"
            checked={draft.is_active}
            onChange={(event) => set('is_active', event.target.checked)}
            className="size-5"
          />
          פעילה, ומוצעת בכרטיס העובד
        </label>

        <div className="flex flex-wrap gap-2">
          <Button
            type="submit"
            variant="primary"
            disabled={isSaving || draft.name.trim() === '' || draft.body.trim() === ''}
          >
            {draft.id === null ? 'יצירת התבנית' : 'שמירת השינויים'}
          </Button>
          <Button variant="secondary" onClick={onCancel}>
            ביטול
          </Button>
        </div>
        <p className="m-0 text-body-sm text-muted">
          שינוי תבנית אינו משנה חוזים שכבר יצאו. הנוסח נשמר מלא ברגע ההוצאה, כי על זה נחתמה
          החתימה.
        </p>
      </form>
    </Panel>
  );
}
